// =====================================================================
// Journal.jsx — M-008 — log des colis (heure, T&T, transporteur, ligne...)
// =====================================================================

import { useState, useEffect, useCallback } from 'react';
import { convoyeur as convoyeurApi } from '../../services/api.js';
import {
  inputBase, thStyle, tdStyle, CarrierChip, fmtWeight, fmtDims, lineLabel, isOfflineError,
} from './styles.jsx';

export default function Journal() {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await convoyeurApi.journal({ q: q.trim() || undefined, limit: 400 });
      setRows(d.rows || []);
      setTotal(d.total || 0);
    } catch (err) {
      setError(isOfflineError(err) ? 'Convoyeur hors-ligne — journal indisponible.' : 'Erreur de chargement.');
      setRows([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.75rem' }}>
        <input
          type="text"
          placeholder="N° suivi, commande, transporteur, L3, 333…"
          value={q}
          onChange={e => setQ(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') load(); }}
          style={{ ...inputBase, flex: '1 1 280px' }}
        />
        <span style={{ marginLeft: 'auto', fontSize: '0.8125rem', color: '#6b7280' }}>
          {rows.length} affiché{rows.length > 1 ? 's' : ''}{total > rows.length ? ` / ${total}` : ''}
        </span>
      </div>

      {error && <div style={{ background: '#fef3c7', color: '#92400e', padding: '0.75rem 1rem', borderRadius: 6, marginBottom: '1rem' }}>{error}</div>}
      {loading && <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Chargement…</div>}

      {!loading && !error && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Heure</th>
                <th style={thStyle}>N° suivi</th>
                <th style={thStyle}>Commande</th>
                <th style={thStyle}>Transporteur</th>
                <th style={thStyle}>Ligne</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Poids</th>
                <th style={thStyle}>Dim</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Contrôle</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={9} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>Aucun colis.</td></tr>
              )}
              {rows.map((r, i) => (
                <tr key={(r.noSuivi || '') + i} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={tdStyle}>{r.date || '—'}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{r.heure || '—'}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.noSuivi || '—'}</td>
                  <td style={tdStyle}>{r.commande || '—'}</td>
                  <td style={tdStyle}><CarrierChip carrier={r.carrier} small /></td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.8125rem' }}>{lineLabel(r.lineNo, r.gravNo)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{fmtWeight(r.weight_g)}</td>
                  <td style={{ ...tdStyle, fontSize: '0.75rem', color: '#6b7280' }}>{fmtDims(r.dims)}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    {r.controle ? (
                      <span title={r.controlePar ? `par ${r.controlePar}` : ''} style={{ color: '#15803d', background: '#dcfce7', fontSize: '0.6875rem', fontWeight: 700, padding: '2px 8px', borderRadius: 6 }}>✓</span>
                    ) : (
                      <span style={{ color: '#b45309', background: '#fef3c7', fontSize: '0.6875rem', fontWeight: 700, padding: '2px 8px', borderRadius: 6 }}>⏳</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
